import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconCalendar,
  IconChartBar,
  IconCircleCheck,
  IconClipboardList,
  IconFileSpreadsheet,
} from "@tabler/icons-react"
import { useState } from "react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

interface SummaryStats {
  total_requests: number
  approved_requests: number
  denied_requests: number
  pending_requests: number
  total_days_approved: number
  average_request_size: number
}

interface MonthlyTrend {
  month: string
  approved: number
  denied: number
  pending: number
}

interface UsageByType {
  [type: string]: {
    requests: number
    days: number
  }
}

interface UserUsage {
  name: string
  avatar_url: string | null
  requests: number
  days: number
}

interface BalanceUtilization {
  user: string
  total_available: number
  total_used: number
  utilization_percentage: number
}

interface Props {
  [key: string]: unknown
  filters: { start_date: string; end_date: string }
  summary_stats: SummaryStats
  monthly_trends: MonthlyTrend[]
  usage_by_type: UsageByType
  usage_by_user: UserUsage[]
  balance_utilization: BalanceUtilization[]
}

const chartConfig: ChartConfig = {
  approved: {
    label: "Approved",
    color: "hsl(142, 76%, 36%)",
  },
  denied: {
    label: "Denied",
    color: "hsl(0, 84%, 60%)",
  },
  pending: {
    label: "Pending",
    color: "hsl(48, 96%, 53%)",
  },
}

function typeLabel(type: string) {
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export default function AdminReportsIndex() {
  const { filters, summary_stats, monthly_trends, usage_by_type, usage_by_user, balance_utilization } =
    usePage<Props>().props

  const [startDate, setStartDate] = useState(filters.start_date)
  const [endDate, setEndDate] = useState(filters.end_date)

  function handleFilter(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/reports", { start_date: startDate, end_date: endDate }, { preserveState: true })
  }

  return (
    <>
      <Head title="Time Off Reports - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Reports</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Time Off Reports</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={`/admin/reports.csv?start_date=${startDate}&end_date=${endDate}`}>
                <IconFileSpreadsheet className="mr-1 h-4 w-4" />
                Export CSV
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={`/admin/reports/calendar_export.ics?start_date=${startDate}&end_date=${endDate}`}>
                <IconCalendar className="mr-1 h-4 w-4" />
                Calendar
              </a>
            </Button>
          </div>
        </div>

        {/* Date Range Filter */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleFilter} className="flex items-end gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input id="start_date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input id="end_date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <Button type="submit">Apply Filter</Button>
            </form>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
              <IconClipboardList className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary_stats.total_requests}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <IconCircleCheck className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary_stats.approved_requests}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Days Taken</CardTitle>
              <IconCalendar className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary_stats.total_days_approved}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Avg Request Size</CardTitle>
              <IconChartBar className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary_stats.average_request_size} days</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Usage by Type */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Usage by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(usage_by_type).map(([type, data]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{typeLabel(type)}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">{data.requests} requests</span>
                      <span className="font-medium">{data.days} days</span>
                    </div>
                  </div>
                ))}
                {Object.keys(usage_by_type).length === 0 && (
                  <p className="text-muted-foreground text-sm text-center py-4">No usage data available.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Users */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Top Users</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Days</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage_by_user.slice(0, 10).map((user) => (
                    <TableRow key={user.name}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell className="text-right">{user.requests}</TableCell>
                      <TableCell className="text-right font-medium">{user.days}</TableCell>
                    </TableRow>
                  ))}
                  {usage_by_user.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground text-center">
                        No data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trends Chart */}
        {monthly_trends.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Monthly Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px] w-full">
                <BarChart data={monthly_trends}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} tickMargin={10} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey="approved" stackId="a" fill="var(--color-approved)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="denied" stackId="a" fill="var(--color-denied)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="pending" stackId="a" fill="var(--color-pending)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}

        {/* Balance Utilization */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Balance Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Utilization</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balance_utilization.map((row) => (
                  <TableRow key={row.user}>
                    <TableCell>{row.user}</TableCell>
                    <TableCell className="text-right">{row.total_available}</TableCell>
                    <TableCell className="text-right">{row.total_used}</TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant={
                          row.utilization_percentage > 80
                            ? "destructive"
                            : row.utilization_percentage > 50
                              ? "secondary"
                              : "outline"
                        }
                      >
                        {row.utilization_percentage}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {balance_utilization.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground text-center">
                      No data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

AdminReportsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
